import CompanyActionsDropdown from "components/company/v2/CompanyActionsDropdown";
import useAmericanIssuer from "components/company/v3/context/hooks/useAmericanIssuer";
import useSelfie from "lib/hooks/useSelfie";
import { IAssetType, ISecurityCountry } from "types/graphqlTypes";
import IndustrySector, { NareitClassificationSector } from "components/company/IndustrySector";
import InPortfolioTag from "components/company/v2/InPortfolioTag";
import InBookmarksTag from "components/company/v2/InBookmarksTag";
import Rating from "components/rating/Rating";
import AmericanExternalLinks from "components/company/v3/american/contents/about/AmericanExternalLinks";
import CompanyDescription from "components/company/v3/CompanyDescription";
import { FormattedMessage } from "react-intl";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";

interface AmericanAboutContentProps {
  tickerSymbol: string;
}

export default function AmericanAboutContent({ tickerSymbol }: AmericanAboutContentProps) {
  const selfie = useSelfie();
  const issuer = useAmericanIssuer();

  return (
    <div className="flex w-full flex-col space-y-6 font-aeonik">
      <div className="flex flex-col items-center space-y-3 md:items-start">
        <div className="flex flex-col justify-center gap-3 md:flex-row md:justify-start md:gap-4">
          {issuer.niceLogo && (
            <div className="flex justify-center md:justify-start">
              <img
                role="presentation"
                src={issuer.niceLogo.url}
                width={54}
                height={54}
                alt={issuer.name}
                draggable={false}
                className="select-none object-contain object-right squircle"
              />
            </div>
          )}

          <div className="flex flex-col space-y-3 md:space-y-0.5">
            <div className="flex w-full flex-col items-center justify-center gap-3 md:flex-row md:justify-start">
              <h1 className="m-0 block overflow-hidden text-ellipsis whitespace-nowrap text-2xl font-bold text-gray-800">
                {issuer.name}
              </h1>

              <div className="flex space-x-3">
                {issuer.isAmericanDepositaryReceipt && (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger>
                        <span
                          title="American Depositary Receipt"
                          className="rounded-lg bg-blue-600 px-2 py-1 text-sm font-bold italic text-white"
                        >
                          ADR
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{getADRTooltipContent(issuer, tickerSymbol)}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {selfie && (
                  <CompanyActionsDropdown
                    issuerId={issuer._id}
                    tickerSymbol={tickerSymbol}
                    securityCountry={ISecurityCountry.Us}
                    viewerDidBookmark={issuer.viewerDidBookmark}
                    isInPortfolio={issuer.isInPortfolio}
                  />
                )}

                {issuer.isInPortfolio && <InPortfolioTag />}
                {issuer.viewerDidBookmark && <InBookmarksTag />}
              </div>
            </div>

            {issuer.isREIT && issuer.nareitClassification && issuer.nareitClassification.propertySector ? (
              <NareitClassificationSector
                propertySector={issuer.nareitClassification.propertySector}
                propertySubsector={issuer.nareitClassification.propertySubsector}
                className="tracking-wide"
              />
            ) : (
              <IndustrySector
                assetType={issuer.isREIT ? IAssetType.AmericanReit : IAssetType.AmericanCompany}
                industrySector={issuer.sector}
                className="tracking-wide"
              />
            )}
          </div>
        </div>

        <AmericanExternalLinks />
      </div>

      <CompanyDescription tickerSymbol={tickerSymbol} country={ISecurityCountry.Us} />

      {selfie?.isPremium && (
        <Rating
          issuer={issuer}
          rankingPlacement={issuer.overallRankingPlacement}
          rankingRatingSummary={issuer.overallRankingRatingSummary}
        />
      )}
    </div>
  );
}

function getADRTooltipContent(issuer: IAmericanCompanyIssuerTopLevelFragment, tickerSymbol: string) {
  const security = issuer.securities.find((candidate) => {
    return candidate.tickerSymbol === tickerSymbol;
  });

  if (security && security.depositoryReceiptRatio) {
    return (
      <FormattedMessage
        defaultMessage="Proporção: {depositoryReceiptRatio}"
        description="Tooltip para tag ADR"
        id="PbxqUV"
        values={{
          depositoryReceiptRatio: security.depositoryReceiptRatio,
        }}
      />
    );
  }
  return "American Depositary Receipt";
}
